import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { useOnboardingSession } from '@/contexts/OnboardingContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/config/supabase';
import type { Step1Data } from '@/types/onboarding';

/**
 * Onboarding Wizard - Step 1: Welcome Screen
 *
 * This is the first step of the onboarding wizard that welcomes users
 * and introduces them to the family tree building journey.
 *
 * Persistence Integration (Phase 1):
 * - On mount: Fetches existing session (if any)
 * - If session exists with current_step > 1: Shows "Resume" option
 * - On "Start" click: Saves Step 1 completion before navigating
 * - Handles loading/error states
 *
 * Design follows the approved Stitch visual direction with:
 * - Center-aligned content with hero image
 * - Badge/label for onboarding journey
 * - Warm, inviting messaging
 * - Feature highlights at bottom
 * - Clear CTA to proceed
 *
 * @see E:\Job\IT\50Studios\Fiels\Clickup_Projects\projects\family-tree\files\stitch\stitch\wizard_welcome_simplified\code.html
 */
export function WelcomeStep() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const {
    session,
    isLoadingSession,
    isSaving,
    saveError,
    saveStep,
    currentStep,
  } = useOnboardingSession();

  // Determine which button to show
  const canResume = session && currentStep > 1;

  const handleStartJourney = async () => {
    try {
      // Save Step 1 completion
      const step1Data: Step1Data = {
        viewedAt: new Date().toISOString(),
      };

      await saveStep(1, step1Data);

      // Navigate to Step 2
      navigate(ROUTES.ONBOARDING_STEP_2);
    } catch (error) {
      console.error('Failed to save Step 1:', error);

      // Show error toast
      toast.error(
        saveError?.message || 'Failed to save progress. Please try again.'
      );
    }
  };

  const handleResume = () => {
    // Navigate to the current step from session
    const stepRoute = `${ROUTES.ONBOARDING}/step-${currentStep}`;
    navigate(stepRoute);
  };

  const handleSkipToEmpty = async () => {
    try {
      // Call skip_onboarding_wizard RPC to mark onboarding as complete
      // This creates NO tree and NO persons - user starts with empty dashboard
      const { data, error } = await supabase.rpc('skip_onboarding_wizard', {
        p_user_id: user?.id,
      } as any);

      if (error) {
        console.error('Failed to skip onboarding:', error);
        throw error;
      }

      // Parse result (RPC returns array with single object)
      const result = (data as unknown as { success: boolean; error_message: string | null }[])[0];

      if (!result || !result.success) {
        throw new Error(result?.error_message || 'Failed to skip onboarding');
      }

      // Success! Refresh profile to update onboarding_completed_at
      // After this completes, the RequireOnboarding guard will detect the updated profile
      // and automatically redirect to dashboard
      console.log('[handleSkipToEmpty] Refreshing user profile to get updated onboarding status...');
      await refreshProfile();
      console.log('[handleSkipToEmpty] Profile refreshed successfully - guards will handle redirect');

      // Show success message
      toast.success('Skipped onboarding. You can now build your tree from scratch.');

      // Let the guard system handle the redirect automatically
    } catch (error) {
      console.error('Failed to skip to dashboard:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to skip onboarding. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 md:px-10 lg:px-20 xl:px-40 py-6 sm:py-8 md:py-12">
      <div className="max-w-[800px] w-full flex flex-col items-center text-center space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
        {/* Header Section */}
        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          {/* Badge */}
          <div className="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-semibold tracking-wide uppercase">
            {t('welcome.badge', 'Onboarding Journey')}
          </div>

          {/* Title */}
          <h1 className="text-slate-900 dark:text-slate-100 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black leading-tight tracking-tight px-2">
            {t('welcome.title', 'Step 1: Welcome')}
          </h1>

          {/* Description */}
          <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed px-2">
            {t(
              'welcome.description',
              "Let's begin your journey by laying the foundation of your family tree. It only takes a few steps to bring your history to life."
            )}
          </p>
        </div>

        {/* Hero Image Section - Real SVG from public/images/ */}
        <div className="w-full max-w-2xl mx-auto">
          <div className="relative group overflow-hidden bg-primary/5 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-primary/10 transition-all duration-300 hover:shadow-2xl hover:border-primary/30">
            {/* Container with aspect ratio */}
            <div className="relative w-full aspect-video md:aspect-[21/9]">
              {/* Background SVG Image - Fills entire container */}
              <div
                className="absolute inset-0 opacity-20 mix-blend-multiply transition-opacity duration-300 group-hover:opacity-30"
                style={{
                  backgroundImage: "url('/images/FamilyTreeOnBoarding.svg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                aria-hidden="true"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent transition-opacity duration-300 group-hover:from-primary/90" aria-hidden="true" />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 md:p-10 text-white">
                <span className="text-xs sm:text-sm font-medium text-white/90 uppercase tracking-wider mb-1 sm:mb-2">
                  {t('welcome.heroLabel', 'Heritage Collection')}
                </span>
                <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black leading-tight tracking-tight text-white">
                  {t('welcome.heroTitle', 'Your Legacy Starts Here')}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-md px-2">
          {/* Show loading state while fetching session */}
          {isLoadingSession ? (
            <div className="flex items-center justify-center gap-2 py-3 sm:py-4">
              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs sm:text-sm text-slate-500">
                {t('welcome.loading', 'Loading...')}
              </span>
            </div>
          ) : (
            <>
              {/* Start button - always shown */}
              <Button
                onClick={handleStartJourney}
                size="lg"
                disabled={isSaving}
                className="flex-1 h-10 sm:h-11 md:h-12 lg:h-14 text-xs sm:text-sm md:text-base lg:text-lg font-bold shadow-lg shadow-primary/20 active:scale-[0.98] transition-transform"
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('welcome.saving', 'Saving...')}
                  </span>
                ) : (
                  t('welcome.cta', 'Start Your Journey')
                )}
              </Button>

              {/* Resume button - only shown if user has progress */}
              {canResume && (
                <Button
                  onClick={handleResume}
                  size="lg"
                  variant="outline"
                  disabled={isSaving}
                  className="flex-1 h-10 sm:h-11 md:h-12 lg:h-14 text-xs sm:text-sm md:text-base lg:text-lg font-bold border-2"
                >
                  {t('welcome.resume', `Resume from Step ${currentStep}`)}
                </Button>
              )}
            </>
          )}
        </div>

        {/* Resume hint - show if user has progress */}
        {canResume && !isLoadingSession && (
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 px-2">
            {t(
              'welcome.resumeHint',
              'You have progress saved. Continue where you left off or start fresh.'
            )}
          </p>
        )}

        {/* Skip path - Start with empty tree */}
        {!isLoadingSession && (
          <div className="flex flex-col items-center gap-2 pt-2 px-2">
            <Button
              onClick={handleSkipToEmpty}
              variant="ghost"
              size="sm"
              disabled={isSaving}
              className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline-offset-4 hover:underline"
            >
              {t('welcome.skipToEmpty', 'Start with an empty tree')}
            </Button>
            <p className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 max-w-sm text-center">
              {t('welcome.skipHint', 'You can always add family members later from your dashboard.')}
            </p>
          </div>
        )}

        {/* Feature Highlights */}
        <div className="flex items-center gap-4 sm:gap-6 md:gap-8 pt-2 sm:pt-4 flex-wrap justify-center px-2">
          <div className="flex flex-col items-center gap-0.5 sm:gap-1 opacity-60 hover:opacity-100 transition-opacity">
            <span className="material-symbols-outlined text-base sm:text-lg md:text-xl lg:text-2xl">verified</span>
            <span className="text-[9px] sm:text-[10px] md:text-xs font-medium uppercase tracking-widest">
              {t('welcome.features.secure', 'Secure')}
            </span>
          </div>

          <div className="flex flex-col items-center gap-0.5 sm:gap-1 opacity-60 hover:opacity-100 transition-opacity">
            <span className="material-symbols-outlined text-base sm:text-lg md:text-xl lg:text-2xl">share</span>
            <span className="text-[9px] sm:text-[10px] md:text-xs font-medium uppercase tracking-widest">
              {t('welcome.features.collaborative', 'Collaborative')}
            </span>
          </div>

          <div className="flex flex-col items-center gap-0.5 sm:gap-1 opacity-60 hover:opacity-100 transition-opacity">
            <span className="material-symbols-outlined text-base sm:text-lg md:text-xl lg:text-2xl">history_edu</span>
            <span className="text-[9px] sm:text-[10px] md:text-xs font-medium uppercase tracking-widest">
              {t('welcome.features.insightful', 'Insightful')}
            </span>
          </div>
        </div>
      </div>
    </main>
  );
}
